import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import GroupCompaniesHome from '../components/company/GroupCompaniesHome';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { VisualBreak } from '../components/ui/VisualBreak';
import { KPIStrip } from '../components/ui/KPIStrip';
import { PowerStatement } from '../components/ui/PowerStatement';
import { BACKEND_URL } from '../config/api';
import { FlowDiagram } from '../components/ui/FlowDiagram';
import { ArrowRight, Shield, Clock, Users, CheckCircle, ClipboardCheck, Truck, Activity, FileText, Settings, X, Download, FileCheck, MapPin, Target, Zap, Droplets, FlaskConical, Wrench, Package, HardHat, AlertTriangle, Wind, Briefcase, Award, Eye } from 'lucide-react';

export const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Full name is required.';
    if (!formData.company.trim()) return 'Company name is required.';
    if (!formData.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) return 'Please enter a valid email address.';
    if (!formData.phone.trim()) return 'Phone number is required.';
    if (!formData.projectType) return 'Please select a project type.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          type: 'vendor-prequalification'
        }),
      });
      
      if (response.ok) {
        setFormSubmitted(true);
        setTimeout(() => {
          setIsModalOpen(false);
          setFormSubmitted(false);
          setFormData({ name: '', company: '', email: '', phone: '', projectType: '' });
        }, 3000);
      } else {
        const data = await response.json().catch(() => null);
        setFormError(data?.error || 'Submission failed. Please try again or contact us directly.');
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SeoHead
        title="EnergiPro Group | Well Testing, Pressure Control & Engineering Services — KSA, UAE, Egypt"
        description="EnergiPro Group delivers well testing, pressure control, stimulation, fabrication, and field engineering services across Saudi Arabia, UAE, and Egypt. ISO 9001/14001/45001 certified."
        canonicalPath="/"
      />
      {/* ===== 1. HERO SECTION — PREMIUM INTEGRATED OILFIELD ===== */}
      <div className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0a1628]">
        {/* Background: Premium equipment yard image with slow zoom parallax */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source srcSet="/assets/hero-equipment-yard-hd.webp" type="image/webp" />
            <img
              src="/assets/hero-equipment-yard-hd.png"
              alt="Active oilfield equipment yard with well testing spreads, pump units, and staging operations at dusk"
              className="w-full h-full object-cover animate-hero-zoom"
              style={{ willChange: 'transform' }}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
          {/* Multi-layer navy overlay for premium depth (75% opacity) */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0f1d32]/80 to-[#0f1d32]/60"></div>
          {/* Vertical gradient for bottom depth */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#060d1a]/90 via-transparent to-[#0a1628]/30"></div>
          {/* Subtle radial glow behind content area */}
          <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-600/5 rounded-full blur-3xl"></div>
        </div>

        {/* Decorative grid pattern overlay for industrial texture */}
        <div className="absolute inset-0 z-[1] opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 60px, rgba(255,255,255,0.1) 60px, rgba(255,255,255,0.1) 61px), repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,255,255,0.1) 60px, rgba(255,255,255,0.1) 61px)' }}></div>

        {/* Content: Left-aligned premium structure */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-28 lg:py-36">
          <div className="max-w-2xl lg:max-w-[60%]">
            {/* Operational tag */}
            <div className="inline-flex items-center gap-2 bg-orange-600/15 border border-orange-500/30 backdrop-blur-sm px-4 py-2 mb-7 animate-fade-in rounded-sm">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <span className="text-orange-400 text-xs font-bold uppercase tracking-wider">Integrated Field Services Provider</span>
            </div>

            {/* Main headline — Sales-focused positioning */}
            <h1 className="font-bold text-white leading-[1.15] mb-5 animate-slide-up" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)' }}>
              <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-[3.6rem]">Integrated Oil & Gas Services</span>
              <span className="block text-[1.4rem] sm:text-[1.8rem] md:text-[2.2rem] lg:text-[2.6rem] text-orange-500 mt-2">Well Testing • Pressure Control • Engineering & Supply</span>
            </h1>


            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-200/90 mb-8 animate-slide-up animation-delay-100 max-w-2xl leading-relaxed">
              Field-ready solutions across KSA & GCC for high-pressure, sour service, and complex operations.
            </p>

            {/* Supporting value props — Sales-focused */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 animate-slide-up animation-delay-200">
              <div className="flex items-center gap-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm px-4 py-3">
                <Shield size={18} className="text-orange-500 shrink-0" />
                <span className="text-slate-200 text-sm">Validated before mobilization</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm px-4 py-3">
                <Activity size={18} className="text-orange-500 shrink-0" />
                <span className="text-slate-200 text-sm">Risk-aware engineering</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm px-4 py-3">
                <Clock size={18} className="text-orange-500 shrink-0" />
                <span className="text-slate-200 text-sm">Pre-execution planning</span>
              </div>
            </div>

            {/* CTA Buttons — Primary conversion focus */}
            <div className="flex flex-col sm:flex-row gap-5 animate-slide-up animation-delay-400">
              <Button size="lg" href="/request-quote" className="!rounded-none !px-12 !py-4 !text-base !font-extrabold !uppercase !tracking-wider !shadow-[0_0_30px_rgba(234,88,12,0.3)] hover:!shadow-[0_0_40px_rgba(234,88,12,0.5)] hover:!-translate-y-0.5 !transition-all !duration-300">
                Request Quote
              </Button>
              <a href="https://wa.me/966552693829?text=Hello%20EnergiPro%2C%20I%20would%20like%20to%20discuss%20a%20technical%20requirement." target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-none px-10 py-4 text-base font-bold uppercase tracking-wider border-2 border-white/40 hover:border-white/80 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 backdrop-blur-sm text-white">
                Talk to Engineering
              </a>
            </div>
          </div>
        </div>

        {/* Right-side accent line */}
        <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-orange-500/40 to-transparent hidden lg:block"></div>
        {/* Bottom edge glow line */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent"></div>
      </div>

      {/* ===== 2. AUTHORITY METRICS STRIP — Operational credibility signals ===== */}
      <div className="relative z-20 -mt-14 mx-4 md:mx-8 lg:mx-auto max-w-6xl">
        <div className="bg-slate-900/95 backdrop-blur-md border border-slate-700/50 border-t-[3px] border-t-orange-500 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6),0_0_15px_-5px_rgba(234,88,12,0.15)]">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {/* 1. Experience */}
            <div className="flex items-center gap-3 px-4 py-5 md:px-5 md:py-6 transition-all duration-300 hover:bg-slate-800/50 relative group">
              <Shield size={20} className="text-orange-500 shrink-0 group-hover:text-orange-400 transition-colors" />
              <span className="text-slate-200 text-sm font-medium leading-tight">20+ Years Combined Field Experience</span>
              <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-slate-700/60 hidden lg:block"></div>
            </div>
            {/* 2. Regional */}
            <div className="flex items-center gap-3 px-4 py-5 md:px-5 md:py-6 transition-all duration-300 hover:bg-slate-800/50 relative group">
              <MapPin size={20} className="text-orange-500 shrink-0 group-hover:text-orange-400 transition-colors" />
              <span className="text-slate-200 text-sm font-medium leading-tight">Operations Across KSA & GCC</span>
              <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-slate-700/60 hidden lg:block"></div>
            </div>
            {/* 3. Full-Service */}
            <div className="flex items-center gap-3 px-4 py-5 md:px-5 md:py-6 transition-all duration-300 hover:bg-slate-800/50 relative group">
              <Users size={20} className="text-orange-500 shrink-0 group-hover:text-orange-400 transition-colors" />
              <span className="text-slate-200 text-sm font-medium leading-tight">Full-Service Field Operations (Equipment + Crew)</span>
              <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-slate-700/60 hidden lg:block"></div>
            </div>
            {/* 4. Equipment */}
            <div className="flex items-center gap-3 px-4 py-5 md:px-5 md:py-6 transition-all duration-300 hover:bg-slate-800/50 relative group">
              <Settings size={20} className="text-orange-500 shrink-0 group-hover:text-orange-400 transition-colors" />
              <span className="text-slate-200 text-sm font-medium leading-tight">Field-Proven Equipment for High-Pressure & Sour Service</span>
              <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-slate-700/60 hidden lg:block"></div>
            </div>
            {/* 5. Mobilization */}
            <div className="flex items-center gap-3 px-4 py-5 md:px-5 md:py-6 transition-all duration-300 hover:bg-slate-800/50 group">
              <Truck size={20} className="text-orange-500 shrink-0 group-hover:text-orange-400 transition-colors" />
              <span className="text-slate-200 text-sm font-medium leading-tight">Rapid Mobilization Capability</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 3. INTERACTIVE ENGINEERING SECTION — Smart RFQ Demo ===== */}
      <Section background="white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Zap size={14} /> Interactive Engineering
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">See How We Engineer Your Job Before Execution</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our Smart RFQ system builds a validated equipment configuration and risk profile for your operation — structured for direct inclusion in tender packages.
            </p>
          </div>

          {/* 3-Step Process */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Step 1 */}
            <div className="relative text-center group">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">1</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Input Your Requirements</h3>
              <p className="text-slate-600 text-sm">Pressure, flow rates, H₂S, sand production, location, and service type — in under 2 minutes.</p>
              {/* Connector arrow (hidden on mobile) */}
              <div className="hidden md:block absolute top-8 left-[65%] w-[70%] h-[2px] bg-gradient-to-r from-orange-300 to-orange-200"></div>
            </div>
            {/* Step 2 */}
            <div className="relative text-center group">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">2</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">System Builds Your Configuration</h3>
              <p className="text-slate-600 text-sm">Engineering rules select equipment, apply safety protocols, and calculate risk levels automatically.</p>
              <div className="hidden md:block absolute top-8 left-[65%] w-[70%] h-[2px] bg-gradient-to-r from-orange-300 to-orange-200"></div>
            </div>
            {/* Step 3 */}
            <div className="text-center group">
              <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">3</div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Get Validated Setup & Risk Profile</h3>
              <p className="text-slate-600 text-sm">Receive equipment configuration, operational confidence score, risk factors, and mitigation plan — instantly.</p>
            </div>
          </div>

          {/* CTA Row */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" href="/request-quote" className="!px-10">
              Launch Smart RFQ
            </Button>
            <Link
              to="/contact?demo=true"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-100 border border-slate-300 rounded-sm text-slate-700 font-bold text-sm hover:bg-slate-200 hover:border-slate-400 transition-all"
            >
              <Activity size={16} className="text-orange-600" />
              Try Demo — See Full Workflow Instantly
            </Link>
          </div>
        </div>
      </Section>
      {/* ===== SERVICES OVERVIEW — ALL 7 LINES ===== */}
      <Section background="light">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">INTEGRATED SERVICE LINES</h2>
          <p className="text-slate-600">
            7 service lines — each engineered and validated through our Smart RFQ system before deployment. All outputs structured for tender submission.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Surface Well Testing */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <Activity className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Surface Well Testing Equipment</h3>
            <p className="text-slate-600 text-sm mb-3">Separators (2/3-phase), choke manifolds, flare booms, data acquisition. Rated: 10K & 1,440 PSI. Use: well cleanup, EWT, production testing.</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW TECHNICAL SCOPE <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>

          {/* Stimulation & Nitrogen */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <Zap className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Stimulation & Nitrogen Equipment</h3>
            <p className="text-slate-600 text-sm mb-3">HP nitrogen units, frac tanks (500 BBL), coiled tubing support. Use: well unloading, pipeline purging, pressure testing, stimulation.</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW EQUIPMENT SPECS <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>

          {/* Field Services */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <Settings className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Field Services & Operational Support</h3>
            <p className="text-slate-600 text-sm mb-3">Rig-up/down crews, wellsite operators, data acquisition technicians. Scope: equipment installation, monitoring, flowback, reporting.</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW TECHNICAL SCOPE <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>

          {/* Heavy & Light Equipment Rental */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <Wrench className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Heavy & Light Equipment Rental</h3>
            <p className="text-slate-600 text-sm mb-3">Cranes (25–100T), generators (50–500 kVA), compressors, forklifts, light towers, vehicles. Short-term and long-term contracts.</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW EQUIPMENT SPECS <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>

          {/* Filtration & Water Treatment */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <Droplets className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Filtration & Water Treatment Systems</h3>
            <p className="text-slate-600 text-sm mb-3">Filtration skids, produced water treatment units, oil/water separation systems. Use: wellsite dewatering, injection water prep, flowback treatment.</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW TECHNICAL SCOPE <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>

          {/* Oilfield Chemicals */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <FlaskConical className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Oilfield Chemicals Supply</h3>
            <p className="text-slate-600 text-sm mb-3">Corrosion inhibitors, scale inhibitors, biocides, demulsifiers, H₂S scavengers. Delivered to site per client treatment program specs.</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW TECHNICAL SCOPE <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>

          {/* Logistics & Transportation */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <div className="bg-orange-100 w-12 h-12 flex items-center justify-center rounded-full mb-4 group-hover:bg-orange-200 transition-colors">
              <Truck className="text-orange-600" size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Logistics & Transportation Support</h3>
            <p className="text-slate-600 text-sm mb-3">Heavy-haul trucks, low-bed trailers, crane trucks. Scope: mob/demob, cross-border coordination, warehousing (KSA, UAE, Egypt).</p>
            <Link to="/services" className="inline-flex items-center text-orange-600 font-bold text-sm hover:text-orange-700">
              VIEW TECHNICAL SCOPE <ArrowRight size={14} className="ml-1" />
            </Link>
          </div>
        </div>
      </Section>

      {/* ===== VISUAL BREAK 1 ===== */}
      <VisualBreak
        image="/assets/showcase/batch-4/desert-wellsite-operations.png"
        statement="Executed under live well conditions across KSA, UAE, and Egypt"
        subtext="Equipment deployed, crews mobilized, data delivered — on schedule."
      />

      {/* ===== 4. KEY EQUIPMENT SECTION ===== */}
      <Section background="white">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">KEY EQUIPMENT — VALIDATED & DEPLOYMENT-READY</h2>
          <p className="text-slate-600">
            Skid-mounted and trailer-mounted configurations in 10K & 1440 PSI ratings. All equipment risk-assessed, pre-validated, and documented for tender inclusion.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm hover:border-orange-300 hover:shadow-md transition-all duration-300">
            <h4 className="font-bold text-slate-900 mb-2">Test Separators</h4>
            <p className="text-slate-600 text-sm">2-phase and 3-phase horizontal separators for well cleanup, extended well testing, and production measurement. Skid & trailer-mounted.</p>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm hover:border-orange-300 hover:shadow-md transition-all duration-300">
            <h4 className="font-bold text-slate-900 mb-2">Choke Manifolds — 10K PSI</h4>
            <p className="text-slate-600 text-sm">Dual-path adjustable and positive choke manifolds rated to 10,000 PSI for wellhead pressure regulation during flow and shut-in.</p>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm hover:border-orange-300 hover:shadow-md transition-all duration-300">
            <h4 className="font-bold text-slate-900 mb-2">Nitrogen Pumping Units</h4>
            <p className="text-slate-600 text-sm">High-pressure nitrogen units for well unloading, pipeline purging, pressure testing, and stimulation support operations.</p>
          </div>
          <div className="bg-slate-900 border border-slate-700 p-5 rounded-sm">
            <h4 className="font-bold text-white mb-2">Frac Tanks & Storage</h4>
            <p className="text-slate-300 text-sm">500 BBL frac tanks for fluid storage and management during well testing, flowback, and stimulation campaigns.</p>
          </div>
          <div className="bg-slate-900 border border-slate-700 p-5 rounded-sm">
            <h4 className="font-bold text-white mb-2">Data Acquisition Systems</h4>
            <p className="text-slate-300 text-sm">Real-time pressure, temperature, and flow monitoring with remote data transmission capability to client SCADA systems.</p>
          </div>
          <div className="bg-slate-900 border border-slate-700 p-5 rounded-sm">
            <h4 className="font-bold text-white mb-2">Flare Systems — H₂S Rated</h4>
            <p className="text-slate-300 text-sm">Multi-burner flare booms with pilot ignition and flame arrestors for safe continuous gas disposal during extended well tests.</p>
          </div>
        </div>
      </Section>

      {/* ===== POWER STATEMENT ===== */}
      <PowerStatement text="Every operation is engineered, validated, and risk-assessed before mobilization." variant="dark" />

      {/* ===== 5. EXECUTION PROCESS FLOW ===== */}
      <Section background="contrast">
        <div className="text-center max-w-3xl mx-auto mb-6">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">PRE-EXECUTION PLANNING MODEL</h2>
          <p className="text-slate-600">
            From RFQ to demobilization — every step validated, risk-assessed, and documented. Outputs structured for tender submission and client evaluation.
          </p>
        </div>

        <FlowDiagram
          steps={[
            { label: 'RFQ Received', detail: 'Technical scope review' },
            { label: 'Proposal', detail: '24 hr response' },
            { label: 'Mobilization', detail: '48–72 hrs to wellsite' },
            { label: 'Rig-Up', detail: 'Equipment installation' },
            { label: 'Operations', detail: 'Monitoring & data' },
            { label: 'Demob & Report', detail: 'Certified deliverables' },
          ]}
        />
      </Section>

      {/* ===== VISUAL BREAK 2 ===== */}
      <VisualBreak
        image="/assets/showcase/batch-4/mobile-separator-trailer-large.png"
        statement="API-aligned execution. No deviation without Management of Change."
        height="sm"
      />

      {/* ===== 6. WHY CHOOSE ENERGIPRO ===== */}
      <Section background="white">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">WHY ENERGIPRO</h2>
          <p className="text-slate-600">
            Not just an equipment supplier — a pre-execution engineering partner. Structured for tender support, validated for deployment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div className="bg-white border-l-4 border-orange-600 p-6 rounded-sm shadow-sm hover:shadow-md transition-shadow">
            <Package className="text-orange-600 mb-3" size={28} />
            <h4 className="font-bold text-slate-900 mb-2">Equipment Ownership</h4>
            <p className="text-slate-600 text-sm">We own our well testing spreads, nitrogen units, and support fleet. No sub-rental dependencies. Equipment maintained and pressure-tested in-house.</p>
          </div>
          <div className="bg-white border-l-4 border-orange-600 p-6 rounded-sm shadow-sm hover:shadow-md transition-shadow">
            <Target className="text-orange-600 mb-3" size={28} />
            <h4 className="font-bold text-slate-900 mb-2">Direct Execution Control</h4>
            <p className="text-slate-600 text-sm">Our crews operate our equipment. Single point of responsibility from rig-up through demob. No third-party operator hand-offs.</p>
          </div>
          <div className="bg-white border-l-4 border-orange-600 p-6 rounded-sm shadow-sm hover:shadow-md transition-shadow">
            <Zap className="text-orange-600 mb-3" size={28} />
            <h4 className="font-bold text-slate-900 mb-2">Mobilization: 48–72 Hrs</h4>
            <p className="text-slate-600 text-sm">Equipment yards in Dammam, Dubai, and Cairo. Pre-staged spreads allow mobilization within 48–72 hours of RFQ acceptance.</p>
          </div>
          <div className="bg-slate-50 border-l-4 border-slate-400 p-6 rounded-sm">
            <FileText className="text-slate-700 mb-3" size={28} />
            <h4 className="font-bold text-slate-900 mb-2">Single Commercial Interface</h4>
            <p className="text-slate-600 text-sm">Equipment + crews + chemicals + logistics on one PO. Reduces vendor coordination overhead for procurement teams.</p>
          </div>
          <div className="bg-slate-50 border-l-4 border-slate-400 p-6 rounded-sm">
            <MapPin className="text-slate-700 mb-3" size={28} />
            <h4 className="font-bold text-slate-900 mb-2">3 Operational Yards</h4>
            <p className="text-slate-600 text-sm">Dammam (KSA), Dubai (UAE), Cairo (Egypt). Equipment staged, crews based locally. No reliance on cross-border mobilization.</p>
          </div>
          <div className="bg-slate-50 border-l-4 border-slate-400 p-6 rounded-sm">
            <Shield className="text-slate-700 mb-3" size={28} />
            <h4 className="font-bold text-slate-900 mb-2">Inspection & Certification Records</h4>
            <p className="text-slate-600 text-sm">All equipment carries current pressure test certificates, third-party inspection reports, and maintenance logs. Available for audit.</p>
          </div>
        </div>
      </Section>

      {/* ===== PROPRIETARY TECHNOLOGY ===== */}
      <Section background="dark">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider mb-4">
            <Zap size={14} /> PROPRIETARY TECHNOLOGY
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Software Solutions for Fleet & Sales Intelligence</h2>
          <p className="text-slate-300 max-w-2xl mx-auto">
            Beyond field services, our team designs and builds software — proven daily in our own operations, and now available for your business. Try either platform live, free, below.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Card 1 — Asset Tracking */}
          <div className="bg-slate-800 border border-slate-700 p-8 rounded-sm hover:border-orange-600/50 transition-colors">
            <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center mb-4">
              <Activity className="text-orange-500" size={24} />
            </div>
            <h4 className="text-white text-xl font-bold mb-3">EnergiPro Asset Tracking</h4>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Fleet and rental management for oilfield operators — track every asset, rental, and maintenance cycle across your yards in real time, from utilization dashboards to overdue-rental alerts.
            </p>
            <a
              href="https://asset-tracking.energiprogroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 rounded-sm text-sm font-semibold transition-colors"
            >
              <Eye size={16} /> Try Live Demo — Free
            </a>
          </div>

          {/* Card 2 — Intelligence Platform */}
          <div className="bg-slate-800 border border-slate-700 p-8 rounded-sm hover:border-orange-600/50 transition-colors">
            <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center mb-4">
              <Activity className="text-orange-500" size={24} />
            </div>
            <h4 className="text-white text-xl font-bold mb-3">EnergiPro Intelligence Platform (EIP)</h4>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              AI-powered sales and technical intelligence that turns drilling data reports into evidence-backed opportunity insights, revenue forecasts, and competitor intelligence — giving technical sales teams a real edge.
            </p>
            <a
              href="https://intelligence.energiprogroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 rounded-sm text-sm font-semibold transition-colors"
            >
              <Eye size={16} /> Try Live Demo — Free
            </a>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/software-solutions"
            className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 font-semibold text-sm uppercase tracking-wide transition-colors"
          >
            Explore Both Platforms in Detail <ArrowRight size={16} />
          </Link>
        </div>
      </Section>

      {/* ===== EXECUTION EXPERIENCE — CREDIBILITY LAYER ===== */}
      <Section background="light">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-orange-400 px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider mb-4">
              <Shield size={14} /> Execution Experience
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">PROVEN IN THE FIELD — NOT JUST ON PAPER</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our team has operated under the toughest conditions the Middle East presents. Every challenge below has been faced, managed, and documented.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* High Pressure Operations */}
            <div className="relative bg-white border border-slate-200 rounded-sm overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img
                  src="/assets/field-oilfield-choke.jpg"
                  alt="10K PSI choke manifold — EnergiPro owned equipment"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="text-orange-600" size={20} />
                  <h3 className="text-lg font-bold text-slate-900">High-Pressure Operations</h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">10,000 PSI well testing, pressure control on live wells, and high-pressure nitrogen operations. Equipment rated, inspected, and certified for extreme duty cycles.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">10K PSI Rated</span>
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">Live Well</span>
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">HP Nitrogen</span>
                </div>
              </div>
            </div>

            {/* Sour Service (H2S) */}
            <div className="relative bg-white border border-slate-200 rounded-sm overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img
                  src="/assets/field-gas-flare.jpg"
                  alt="H2S-rated flare boom system — EnergiPro owned equipment"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="text-orange-600" size={20} />
                  <h3 className="text-lg font-bold text-slate-900">Sour Service (H₂S)</h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">Full H₂S-rated operations including NACE-compliant equipment, breathing apparatus, evacuation protocols, and continuous gas monitoring. Crews certified for sour environments.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">NACE Compliant</span>
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">H₂S Rated</span>
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">Gas Detection</span>
                </div>
              </div>
            </div>

            {/* Sand-Heavy Environments */}
            <div className="relative bg-white border border-slate-200 rounded-sm overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="h-48 overflow-hidden">
                <img
                  src="/assets/field-oil-rig.jpg"
                  alt="Desert wellsite operations — EnergiPro field execution in sand-heavy environment"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Wind className="text-orange-600" size={20} />
                  <h3 className="text-lg font-bold text-slate-900">Sand-Heavy Environments</h3>
                </div>
                <p className="text-slate-600 text-sm mb-4">Desander systems, erosion-resistant internals, and sand management protocols for high-sand-production wells. Regular operations in Eastern Province and Abu Dhabi fields.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">Desanding</span>
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">Erosion Control</span>
                  <span className="px-2 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold rounded-sm">Filtration</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== TEAM PRESENCE — TECHNICAL TEAM ===== */}
      <Section background="white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="relative">
              <picture>
                <source srcSet="/assets/team-field-engineer.webp" type="image/webp" />
                <img
                  src="/assets/team-field-engineer.png"
                  alt="EnergiPro Solutions field engineer at oilfield operations site"
                  className="w-full rounded-sm shadow-lg"
                />
              </picture>
              <div className="absolute -bottom-4 -right-4 bg-orange-600 text-white px-6 py-3 rounded-sm shadow-lg hidden md:block">
                <span className="text-sm font-bold uppercase tracking-wider">Experienced Crew</span>
              </div>
            </div>

            {/* Content Side */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                <Users size={14} /> Technical Team
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">FIELD ENGINEERS & SUPERVISORS WHO'VE BEEN THERE</h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our technical staff have operated across Saudi Arabia, UAE, Egypt, and Kuwait — under operator supervision from major NOCs and IOCs. They don't just know the procedures; they've executed them under pressure.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center shrink-0">
                    <HardHat className="text-orange-500" size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Field Supervisors</h4>
                    <p className="text-slate-600 text-sm">Senior supervisors with 10–20+ years on wellsites. Experienced in multi-well campaigns, sour operations, and offshore deployments.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center shrink-0">
                    <Settings className="text-orange-500" size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Operations Engineers</h4>
                    <p className="text-slate-600 text-sm">Equipment configuration, pressure calculations, risk assessment, and real-time operational decisions. Trained on API and ASME standards.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center shrink-0">
                    <Activity className="text-orange-500" size={18} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Data Acquisition Technicians</h4>
                    <p className="text-slate-600 text-sm">Real-time monitoring, flow measurement, sampling, and report generation. Data delivered to client spec, on time.</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6 pt-4 border-t border-slate-200">
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-slate-900">40+</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Technical Staff</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-slate-900">3</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-extrabold text-slate-900">24/7</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Field Coverage</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== KPI STRIP ===== */}
      <KPIStrip
        background="accent"
        items={[
          { value: '10K', suffix: ' PSI', label: 'Equipment Pressure Rating' },
          { value: '48–72', suffix: ' hrs', label: 'Mobilization Time' },
          { value: '75', suffix: '+', label: 'Projects Executed' },
          { value: '120', suffix: '+', label: 'Equipment Units' },
        ]}
      />

      {/* ===== 6B. ENERGIPRO GROUP COMPANIES ===== */}
      <GroupCompaniesHome />

      {/* ===== 7. SELECTED CLIENTS & PARTNERS ===== */}
      <Section background="white">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">CLIENTS & PARTNERS</h2>
          <p className="text-slate-600">
            Organizations we have provided equipment or services to, directly or through subcontract arrangements.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {['Sinopec', 'SGS', 'NAPESCO', 'Expro', 'Qemat Najd', 'XEED Energy Solutions', 'OXY', 'Zener Fire & Security', 'Kuwait Oil Company', 'Al-Futtaim Engineering', 'Chevron', 'KONE', 'El Seif', 'Kuwait Gulf Oil Company', 'Shapoorji Pallonji', 'Risal (Rawabi)'].map((client) => (
            <div key={client} className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-center h-20 hover:border-orange-300 hover:shadow-sm transition-all duration-300">
              <span className="text-slate-700 font-bold text-xs text-center uppercase tracking-wide">{client}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-slate-400 text-xs mt-4 max-w-3xl mx-auto">Client references and project documentation available upon request during prequalification. Logos shown represent companies served directly or via subcontract — not endorsement.</p>
      </Section>

      {/* ===== VISUAL BREAK 3 ===== */}
      <VisualBreak
        image="/assets/showcase/batch-3/logistics-trucks.png"
        statement="Structured operations. Audit-ready documentation. Zero ambiguity."
        height="sm"
      />

      {/* ===== 8. PROJECT SNAPSHOT ===== */}
      <Section background="light">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">PROJECT SNAPSHOT</h2>
          <p className="text-slate-600">
            Representative execution from our operational portfolio.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="border-l-4 border-orange-600 p-6 md:p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Surface Well Testing — Multi-Well Campaign</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Pressure Rating</span>
                  <span className="text-sm text-slate-900 font-medium">10,000 PSI working pressure</span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Equipment Deployed</span>
                  <span className="text-sm text-slate-900 font-medium">3-phase separator, 10K choke manifold, flare boom, data acquisition unit</span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Execution Scale</span>
                  <span className="text-sm text-slate-900 font-medium">5 wells, 45 days continuous, Eastern Province KSA</span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Outcome</span>
                  <span className="text-sm text-slate-900 font-medium">Zero NPT, data delivered on schedule</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="border-l-4 border-slate-300 p-6 md:p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Nitrogen Purging & Pipeline Testing</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Pressure Rating</span>
                  <span className="text-sm text-slate-900 font-medium">Test pressure: 1,440 PSI</span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Equipment Deployed</span>
                  <span className="text-sm text-slate-900 font-medium">3× HP nitrogen pump units, chart recorders, manifold assembly</span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Execution Scale</span>
                  <span className="text-sm text-slate-900 font-medium">12 km pipeline, 21 days, Abu Dhabi UAE</span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Outcome</span>
                  <span className="text-sm text-slate-900 font-medium">Passed first attempt, certified charts delivered</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button href="/projects" variant="outline">
              VIEW FULL PROJECT PORTFOLIO
            </Button>
          </div>
        </div>
      </Section>

      {/* ===== CONVERSION CTA STRIP ===== */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-700 py-10">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-1">Ready to see your operation engineered?</h3>
            <p className="text-orange-100/80 text-sm">Get a pre-execution validated configuration and risk profile — ready for tender submission.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button href="/request-quote" className="!bg-white !text-orange-700 hover:!bg-orange-50 !font-bold !px-6">
              Request Technical Proposal
            </Button>
            <Button href="/contact?demo=true" variant="outline-light" className="!border-white/50 !text-white hover:!bg-white/10 !font-bold !px-6">
              Try Demo
            </Button>
          </div>
        </div>
      </div>

      {/* ===== 9. COMPLIANCE & PREQUALIFICATION — ENHANCED ===== */}
      <Section background="dark">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">COMPLIANCE & MANAGEMENT SYSTEMS</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Our Integrated Management System ensures every operation meets international standards. Structured for audit, documented for traceability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {/* IMS System */}
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center">
                  <Award className="text-orange-500" size={24} />
                </div>
                <h4 className="text-white font-bold">Integrated Management System</h4>
              </div>
              <p className="text-slate-400 text-sm mb-4">IMS certified to ISO 9001:2015, ISO 14001:2015, and ISO 45001:2018 — full third-party certification achieved. Single system governing quality, environment, and occupational health & safety.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Quality Policy & Objectives
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Document Control Procedures
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Management Review & Continual Improvement
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Non-Conformance & Corrective Action
                </li>
              </ul>
            </div>

            {/* HSE Procedures */}
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center">
                  <Shield className="text-orange-500" size={24} />
                </div>
                <h4 className="text-white font-bold">HSE Procedures</h4>
              </div>
              <p className="text-slate-400 text-sm mb-4">Comprehensive health, safety, and environmental procedures for every operation. Toolbox talks, JSA reviews, and incident reporting built into execution workflow.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Job Safety Analysis (JSA)
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Permit to Work System
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Emergency Response Plans
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Incident Investigation & Reporting
                </li>
              </ul>
            </div>

            {/* QA/QC Structure */}
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm hover:border-orange-600/50 transition-colors">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-600/20 rounded-full flex items-center justify-center">
                  <Eye className="text-orange-500" size={24} />
                </div>
                <h4 className="text-white font-bold">QA/QC Structure</h4>
              </div>
              <p className="text-slate-400 text-sm mb-4">Dedicated QA/QC personnel on every project. Equipment inspection, calibration records, and material traceability maintained throughout the project lifecycle.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Equipment Inspection & Certification
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Third-Party Verification
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Calibration & Maintenance Logs
                </li>
                <li className="flex items-center gap-2 text-slate-300 text-xs">
                  <CheckCircle size={12} className="text-orange-500 shrink-0" />
                  Material Traceability (MTR)
                </li>
              </ul>
            </div>
          </div>

          {/* HSE Data Bar */}
          <div className="bg-slate-900 border border-slate-600 rounded-sm p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-extrabold text-orange-500">0.00</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">TRIR (Target)</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-orange-500">100%</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">JSA Compliance</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-orange-500">Daily</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Toolbox Talks</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-orange-500">Zero</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">Environmental Incidents</div>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <Button href="/files/energipro-capability-statement.pdf" variant="primary" size="lg">
              <Download className="shrink-0 mr-2" size={18} />
              DOWNLOAD FULL CAPABILITY & PREQUALIFICATION PACKAGE
            </Button>
          </div>
        </div>
      </Section>

      {/* ===== COMMERCIAL STAGES — RFQ PIPELINE ===== */}
      <Section background="light">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-orange-400 px-4 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider mb-4">
              <Briefcase size={14} /> Commercial Process
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">FROM RFQ TO CONTRACT AWARD</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our commercial process is structured, transparent, and designed to support your procurement workflow — from first inquiry to signed contract.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Stage 1 */}
            <div className="relative text-center group">
              <div className="w-20 h-20 bg-white border-2 border-orange-600 rounded-full flex flex-col items-center justify-center mx-auto mb-5 shadow-lg group-hover:bg-orange-600 group-hover:text-white transition-all duration-300">
                <FileText size={22} className="text-orange-600 group-hover:text-white transition-colors" />
                <span className="text-[10px] font-bold text-orange-600 group-hover:text-white mt-0.5">01</span>
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">Proposal Generated</h3>
              <p className="text-slate-600 text-xs leading-relaxed">Technical scope received, engineering review completed, and preliminary proposal issued within 24 hours.</p>
              {/* Connector */}
              <div className="hidden md:block absolute top-10 left-[65%] w-[70%] h-[2px] bg-gradient-to-r from-orange-400 to-orange-200"></div>
            </div>

            {/* Stage 2 */}
            <div className="relative text-center group">
              <div className="w-20 h-20 bg-white border-2 border-orange-600 rounded-full flex flex-col items-center justify-center mx-auto mb-5 shadow-lg group-hover:bg-orange-600 group-hover:text-white transition-all duration-300">
                <ClipboardCheck size={22} className="text-orange-600 group-hover:text-white transition-colors" />
                <span className="text-[10px] font-bold text-orange-600 group-hover:text-white mt-0.5">02</span>
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">Commercial Proposal</h3>
              <p className="text-slate-600 text-xs leading-relaxed">Full commercial offer with pricing, payment terms, mobilization schedule, and contract structure — formatted for procurement teams.</p>
              <div className="hidden md:block absolute top-10 left-[65%] w-[70%] h-[2px] bg-gradient-to-r from-orange-400 to-orange-200"></div>
            </div>

            {/* Stage 3 */}
            <div className="relative text-center group">
              <div className="w-20 h-20 bg-white border-2 border-orange-600 rounded-full flex flex-col items-center justify-center mx-auto mb-5 shadow-lg group-hover:bg-orange-600 group-hover:text-white transition-all duration-300">
                <Users size={22} className="text-orange-600 group-hover:text-white transition-colors" />
                <span className="text-[10px] font-bold text-orange-600 group-hover:text-white mt-0.5">03</span>
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">Negotiation</h3>
              <p className="text-slate-600 text-xs leading-relaxed">Technical clarification, commercial alignment, and scope finalization. Our team is responsive and flexible on terms.</p>
              <div className="hidden md:block absolute top-10 left-[65%] w-[70%] h-[2px] bg-gradient-to-r from-orange-400 to-orange-200"></div>
            </div>

            {/* Stage 4 */}
            <div className="text-center group">
              <div className="w-20 h-20 bg-orange-600 border-2 border-orange-600 rounded-full flex flex-col items-center justify-center mx-auto mb-5 shadow-lg shadow-orange-500/30">
                <Award size={22} className="text-white" />
                <span className="text-[10px] font-bold text-white mt-0.5">04</span>
              </div>
              <h3 className="text-base font-bold text-slate-900 mb-2">Contract Award</h3>
              <p className="text-slate-600 text-xs leading-relaxed">PO issued, mobilization triggered, crews assigned. Equipment on-site within 48–72 hours of contract execution.</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button href="/request-quote" size="lg">
              Start Your RFQ Process
            </Button>
          </div>
        </div>
      </Section>

      {/* ===== VISUAL BREAK 4 ===== */}
      <VisualBreak
        image="/assets/showcase/batch-4/wellsite-separator-surge-tank.png"
        statement="Owned equipment. Our crews. Single point of accountability."
        height="md"
      />

      {/* ===== 10. FINAL CTA ===== */}
      <Section background="navy" className="border-t-2 border-orange-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            START YOUR PRE-EXECUTION ENGINEERING REVIEW
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            Get a validated equipment configuration, risk profile, and confidence score — structured for tender submission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Button size="lg" href="/request-quote">
              REQUEST TECHNICAL PROPOSAL
            </Button>
            <Button size="lg" variant="outline-light" href="/request-quote">
              START SMART RFQ
            </Button>
            <Button 
              size="lg" 
              variant="outline-light" 
              className="gap-2"
              href="/request-quote"
            >
              BOOK ENGINEERING DISCUSSION
            </Button>
          </div>
        </div>
      </Section>

      {/* Modal for Vendor Prequalification */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          <div className="relative bg-white rounded-sm shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Vendor Prequalification Request</h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {formSubmitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-green-600" size={32} />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Thank You</h4>
                <p className="text-slate-600">Our vendor prequalification package will be sent to your email.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm flex items-start gap-2">
                    <svg className="w-5 h-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{formError}</span>
                  </div>
                )}
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-slate-700 mb-2">
                    Name <span className="text-orange-600">*</span>
                  </label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 border-l-2 border-l-orange-600 rounded-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-bold text-slate-700 mb-2">
                    Company <span className="text-orange-600">*</span>
                  </label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 border-l-2 border-l-orange-600 rounded-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
                    placeholder="Enter company name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-2">
                    Email <span className="text-orange-600">*</span>
                  </label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 border-l-2 border-l-orange-600 rounded-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
                    placeholder="Enter email address"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-slate-700 mb-2">
                    Phone <span className="text-orange-600">*</span>
                  </label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 border-l-2 border-l-orange-600 rounded-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
                    placeholder="Enter phone number"
                  />
                </div>
                
                <div>
                  <label htmlFor="projectType" className="block text-sm font-bold text-slate-700 mb-2">
                    Project Type <span className="text-orange-600">*</span>
                  </label>
                  <select 
                    id="projectType" 
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-300 border-l-2 border-l-orange-600 rounded-sm focus:outline-none focus:border-orange-600 focus:ring-1 focus:ring-orange-600"
                  >
                    <option value="">Select project type</option>
                    <option value="well-testing">Well Testing & Production Systems</option>
                    <option value="pumping-stimulation">Pumping, Stimulation & Process Support</option>
                    <option value="engineering-fabrication">Engineering, Fabrication & Package Integration</option>
                    <option value="chemicals-filtration">Chemicals, Filtration & Water Treatment</option>
                    <option value="equipment-supply">Equipment Supply & Industrial Solutions</option>
                    <option value="logistics-field-ops">Logistics & Field Operations Support</option>
                    <option value="digital-data">Digital & Data Solutions</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT REQUEST'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};
